import { useEffect, useState, useRef, useCallback, type FormEvent, type KeyboardEvent } from 'react';
import { marked } from 'marked';
import {
  sendMessage,
  listConversations,
  getConversation,
  archiveConversation,
  validateToken,
  setToken,
  getToken,
  clearToken,
  getBalances,
  ApiException,
} from './api';
import type { ProviderBalance } from './api';
import type { ConversationSummary, Message } from './types';

marked.setOptions({ breaks: true, gfm: true });

export default function App() {
  const [hasToken, setHasToken] = useState<boolean>(() => !!getToken());

  // Stable callbacks — recreating them each render would invalidate child useCallback deps
  // and cause infinite re-render loops in ChatApp.
  const handleSetupComplete = useCallback(() => setHasToken(true), []);
  const handleLogout = useCallback(() => { clearToken(); setHasToken(false); }, []);

  if (!hasToken) {
    return <Setup onComplete={handleSetupComplete} />;
  }

  return <ChatApp onLogout={handleLogout} />;
}

/* ─────────────────────────────────────────────────────────────────────────
   Setup — first run: prompt for shared secret, validate, save to localStorage
─────────────────────────────────────────────────────────────────────────── */

function Setup({ onComplete }: { onComplete: () => void }) {
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!secret.trim()) {
      setError('Secret cannot be empty');
      return;
    }
    setBusy(true);
    const ok = await validateToken(secret.trim());
    setBusy(false);
    if (!ok) {
      setError('Invalid secret. Check that it matches the SHARED_SECRET set on the Worker.');
      return;
    }
    setToken(secret.trim());
    onComplete();
  }

  return (
    <div className="h-full flex items-center justify-center px-6 pt-safe pb-safe">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🤖</div>
          <h1 className="text-2xl font-semibold">Felix</h1>
          <p className="text-sm text-neutral-400 mt-1">Enter your shared secret to begin</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            inputMode="text"
            autoComplete="off"
            placeholder="Shared secret"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            className="w-full px-4 py-3 bg-neutral-900 border border-neutral-800 rounded-lg text-sm font-mono focus:outline-none focus:border-neutral-600"
            disabled={busy}
          />
          {error && <div className="text-xs text-red-400 px-1">{error}</div>}
          <button
            type="submit"
            disabled={busy || !secret.trim()}
            className="w-full py-3 bg-neutral-100 text-neutral-950 rounded-lg text-sm font-medium hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Verifying…' : 'Continue'}
          </button>
        </form>
        <p className="text-xs text-neutral-500 mt-6 text-center leading-relaxed">
          Token stored locally on this device only.<br />
          Never leaves your browser except in the Authorization header.
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   ChatApp — main two-pane layout: conversation list + active chat
─────────────────────────────────────────────────────────────────────────── */

function ChatApp({ onLogout }: { onLogout: () => void }) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [view, setView] = useState<'chat' | 'balances'>('chat');

  const reloadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const items = await listConversations();
      setConversations(items);
    } catch (err) {
      console.error('Failed to list conversations:', err);
      if (err instanceof ApiException && err.status === 401) {
        onLogout();
      }
    } finally {
      setLoadingList(false);
    }
  }, [onLogout]);

  useEffect(() => { reloadConversations(); }, [reloadConversations]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    let cancelled = false;
    setLoadingConvo(true);
    getConversation(activeId)
      .then((data) => { if (!cancelled) setMessages(data.messages); })
      .catch((err) => {
        console.error('Failed to load conversation:', err);
        if (!cancelled && err instanceof ApiException && err.status === 404) {
          setActiveId(null);
          setMessages([]);
        }
      })
      .finally(() => { if (!cancelled) setLoadingConvo(false); });
    return () => { cancelled = true; };
  }, [activeId]);

  function startNewConversation() {
    setActiveId(null);
    setMessages([]);
    setSidebarOpen(false);
  }

  async function handleSend(text: string) {
    // Optimistically add the user message
    const tempUserMsg: Message = {
      id: 'temp-' + Date.now(),
      conversation_id: activeId || 'temp',
      role: 'user',
      content: text,
      created_at: Date.now(),
      model: null,
      input_tokens: null,
      output_tokens: null,
    };
    setMessages((m) => [...m, tempUserMsg]);

    try {
      const res = await sendMessage(activeId, text);
      const assistantMsg: Message = {
        id: res.assistant_message_id,
        conversation_id: res.conversation_id,
        role: 'assistant',
        content: res.response,
        created_at: Date.now(),
        model: res.model,
        input_tokens: res.usage.input_tokens,
        output_tokens: res.usage.output_tokens,
      };
      const realUserMsg: Message = {
        ...tempUserMsg,
        id: res.user_message_id,
        conversation_id: res.conversation_id,
      };
      setMessages((m) =>
        m.map((msg) => (msg.id === tempUserMsg.id ? realUserMsg : msg)).concat(assistantMsg)
      );
      if (!activeId) setActiveId(res.conversation_id);
      // Refresh sidebar to show new/updated conversation
      reloadConversations();
    } catch (err) {
      console.error('Send failed:', err);
      const errorMsg: Message = {
        id: 'error-' + Date.now(),
        conversation_id: activeId || 'temp',
        role: 'assistant',
        content: `**Error:** ${err instanceof Error ? err.message : 'Unknown'}. Tap retry or check Worker logs.`,
        created_at: Date.now(),
        model: null,
        input_tokens: null,
        output_tokens: null,
      };
      setMessages((m) => [...m, errorMsg]);
    }
  }

  async function handleArchive(id: string) {
    if (!confirm('Archive this conversation?')) return;
    try {
      await archiveConversation(id);
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
      reloadConversations();
    } catch (err) {
      console.error('Archive failed:', err);
    }
  }

  return (
    <div className="h-full flex pt-safe pb-safe">
      {/* Sidebar — fixed on mobile (overlay), persistent on desktop */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversations}
        activeId={activeId}
        loading={loadingList}
        onSelect={(id) => { setActiveId(id); setView('chat'); setSidebarOpen(false); }}
        onNew={startNewConversation}
        onArchive={handleArchive}
        onRefresh={reloadConversations}
        onLogout={onLogout}
        view={view}
        onViewChange={(v) => { setView(v); setSidebarOpen(false); }}
      />

      {/* Main area */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 px-4 py-3 border-b border-neutral-800 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded hover:bg-neutral-800"
            aria-label="Open menu"
          >
            <HamburgerIcon />
          </button>
          <div className="flex-1 text-sm font-medium truncate">
            {view === 'balances'
              ? 'Balances'
              : activeId
                ? conversations.find((c) => c.id === activeId)?.title || 'Conversation'
                : 'New conversation'}
          </div>
          {view === 'chat' && (
            <button
              onClick={startNewConversation}
              className="p-2 -mr-2 rounded hover:bg-neutral-800"
              aria-label="New conversation"
            >
              <PlusIcon />
            </button>
          )}
        </header>

        {view === 'chat' ? (
          <>
            <MessageList messages={messages} loading={loadingConvo} />
            <Composer onSend={handleSend} disabled={loadingConvo} />
          </>
        ) : (
          <BalancesView />
        )}
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Sidebar — conversation list
─────────────────────────────────────────────────────────────────────────── */

function Sidebar(props: {
  open: boolean;
  onClose: () => void;
  conversations: ConversationSummary[];
  activeId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onArchive: (id: string) => void;
  onRefresh: () => void;
  onLogout: () => void;
  view: 'chat' | 'balances';
  onViewChange: (v: 'chat' | 'balances') => void;
}) {
  return (
    <>
      {/* Overlay backdrop on mobile */}
      {props.open && (
        <button
          className="lg:hidden fixed inset-0 bg-black/60 z-10"
          onClick={props.onClose}
          aria-label="Close sidebar"
        />
      )}
      <aside
        className={`
          fixed lg:relative top-0 left-0 h-full w-72 lg:w-64
          bg-neutral-950 lg:bg-neutral-925 border-r border-neutral-800
          z-20 transition-transform duration-200 ease-out
          pt-safe pb-safe
          ${props.open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* View nav toggle */}
        <div className="px-2 py-2 border-b border-neutral-800 flex gap-1">
          <button
            onClick={() => props.onViewChange('chat')}
            className={`flex-1 text-sm px-3 py-2 rounded transition-colors ${
              props.view === 'chat' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-400 hover:bg-neutral-900'
            }`}
          >
            💬 Chat
          </button>
          <button
            onClick={() => props.onViewChange('balances')}
            className={`flex-1 text-sm px-3 py-2 rounded transition-colors ${
              props.view === 'balances' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-400 hover:bg-neutral-900'
            }`}
          >
            💰 Balances
          </button>
        </div>

        <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
          <span className="text-sm font-semibold">Conversations</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={props.onRefresh}
              className="text-neutral-400 hover:text-neutral-100 p-1 rounded hover:bg-neutral-800 transition-colors"
              aria-label="Refresh conversations"
              title="Refresh"
            >
              <RefreshIcon />
            </button>
            <button
              onClick={props.onNew}
              className="text-xs px-2 py-1 bg-neutral-800 hover:bg-neutral-700 rounded transition-colors"
            >
              + New
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {props.loading && (
            <div className="px-4 py-3 text-xs text-neutral-500">Loading…</div>
          )}
          {!props.loading && props.conversations.length === 0 && (
            <div className="px-4 py-3 text-xs text-neutral-500">
              No conversations yet. Type a message below to start.
            </div>
          )}
          {props.conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => props.onSelect(c.id)}
              className={`
                group w-full text-left px-4 py-2.5 hover:bg-neutral-900 transition-colors
                ${props.activeId === c.id ? 'bg-neutral-900' : ''}
              `}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{c.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {formatRelative(c.updated_at)} · {c.message_count} msg
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); props.onArchive(c.id); }}
                  className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400 text-xs px-1 transition-opacity"
                  aria-label="Archive"
                >
                  ✕
                </button>
              </div>
            </button>
          ))}
        </div>

        <div className="border-t border-neutral-800 p-2">
          <button
            onClick={props.onLogout}
            className="w-full text-left px-3 py-2 text-xs text-neutral-500 hover:text-neutral-300 hover:bg-neutral-900 rounded transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MessageList — render messages with auto-scroll
─────────────────────────────────────────────────────────────────────────── */

function MessageList({ messages, loading }: { messages: Message[]; loading: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-neutral-500">
        Loading conversation…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
        <div className="text-5xl mb-3">👋</div>
        <p className="text-sm text-neutral-400 max-w-xs">
          Start a conversation with Felix. He won't have access to your Mac files in this PWA channel — for tool-capable work, use Telegram or Cowork.
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} />
      ))}
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  const html = isUser ? null : { __html: marked.parse(message.content) as string };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          max-w-[85%] rounded-2xl px-4 py-2.5
          ${isUser
            ? 'bg-blue-600 text-white rounded-br-sm'
            : 'bg-neutral-900 text-neutral-100 rounded-bl-sm border border-neutral-800'}
        `}
      >
        {isUser ? (
          <div className="text-sm whitespace-pre-wrap">{message.content}</div>
        ) : (
          <div className="markdown" dangerouslySetInnerHTML={html!} />
        )}
        {!isUser && message.input_tokens != null && (
          <div className="text-[10px] text-neutral-500 mt-2">
            {message.model} · {message.input_tokens}→{message.output_tokens} tok
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   BalancesView — multi-provider spend dashboard
─────────────────────────────────────────────────────────────────────────── */

function BalancesView() {
  const [days, setDays] = useState(7);
  const [providers, setProviders] = useState<ProviderBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBalances(d);
      setProviders(data.providers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Provider Spend</h2>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="text-xs bg-neutral-900 border border-neutral-800 rounded px-2 py-1.5 outline-none"
          >
            <option value={1}>24h</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
          </select>
          <button
            onClick={() => load(days)}
            className="text-neutral-400 hover:text-neutral-100 p-1.5 rounded hover:bg-neutral-800 transition-colors"
            aria-label="Refresh balances"
          >
            <RefreshIcon />
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>}
      {error && <div className="text-sm text-red-400 py-4">{error}</div>}

      {!loading && !error && (
        <div className="space-y-3">
          {providers.map((p) => (
            <ProviderCard key={p.provider} p={p} />
          ))}
        </div>
      )}

      <p className="text-[11px] text-neutral-600 mt-6 leading-relaxed">
        Anthropic spend is live via Cloudflare AI Gateway analytics. OpenAI shows live usage when its key is set.
        Gemini + Brave link to their dashboards (API wiring in v1.1). Auto-topup logic is intentionally not built —
        spend changes stay human-in-the-loop.
      </p>
    </div>
  );
}

function ProviderCard({ p }: { p: ProviderBalance }) {
  const label = p.provider.charAt(0).toUpperCase() + p.provider.slice(1);
  const statusColor =
    p.status === 'ok' ? 'text-green-400' : p.status === 'unavailable' ? 'text-amber-400' : 'text-neutral-500';
  const statusLabel =
    p.status === 'ok' ? 'live' : p.status === 'unavailable' ? 'unavailable' : 'not wired';

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        <span className={`text-[11px] ${statusColor}`}>● {statusLabel}</span>
      </div>

      {p.status === 'ok' && (
        <div className="space-y-1">
          {p.spend_usd != null && (
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold">${p.spend_usd.toFixed(2)}</span>
              <span className="text-xs text-neutral-500">/ {p.window}</span>
            </div>
          )}
          {(p.requests != null || p.tokens_in != null) && (
            <div className="text-xs text-neutral-500">
              {p.requests != null && `${p.requests.toLocaleString()} requests`}
              {p.tokens_in != null && ` · ${((p.tokens_in + (p.tokens_out ?? 0)) / 1000).toFixed(1)}k tokens`}
            </div>
          )}
        </div>
      )}

      {p.status !== 'ok' && (
        <div className="text-xs text-neutral-400">
          {p.note && <div className="mb-2">{p.note}</div>}
          {p.dashboard_url && (
            <a
              href={p.dashboard_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline"
            >
              Open provider dashboard ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Composer — text input + send button
─────────────────────────────────────────────────────────────────────────── */

function Composer({ onSend, disabled }: { onSend: (text: string) => void | Promise<void>; disabled: boolean }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText('');
    try {
      await onSend(t);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }

  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
  }, [text]);

  return (
    <form onSubmit={handleSubmit} className="border-t border-neutral-800 p-3">
      <div className="flex items-end gap-2 bg-neutral-900 border border-neutral-800 rounded-2xl p-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Message Felix…"
          rows={1}
          disabled={disabled || sending}
          className="flex-1 bg-transparent resize-none outline-none text-sm px-2 py-1.5 placeholder-neutral-500"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending || disabled}
          className="
            shrink-0 w-9 h-9 rounded-full bg-neutral-100 text-neutral-950
            flex items-center justify-center
            hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors
          "
          aria-label="Send"
        >
          {sending ? <SpinnerIcon /> : <ArrowUpIcon />}
        </button>
      </div>
      <div className="text-[10px] text-neutral-600 mt-1.5 px-2">
        Enter to send · Shift+Enter for newline
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Icons (inline SVG, no external deps)
─────────────────────────────────────────────────────────────────────────── */

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" className="animate-spin">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25" />
      <path d="M12 3 a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Utils
─────────────────────────────────────────────────────────────────────────── */

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return Math.floor(diff / 60_000) + 'm';
  if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + 'h';
  if (diff < 7 * 86_400_000) return Math.floor(diff / 86_400_000) + 'd';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
